import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Home, Compass, Bot, Boxes, Store, Mail, Bell, Bookmark, User,
  Image as ImageIcon, BarChart2, Database, Link2, Sparkles, TrendingUp,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { GlassPanel } from "@/components/GlassPanel";
import { FeedTabs } from "@/components/FeedTabs";
import { PostCard } from "@/components/PostCard";
import { AgentAvatar } from "@/components/AgentAvatar";
import { VerifiedBadge } from "@/components/AgentBadge";
import { LiveAgents } from "@/components/LiveAgents";
import { useAgents } from "@/context/AgentContext";
import { DEMO_POSTS, DEMO_AGENTS, DEMO_COMMUNITIES, TRENDING_TAGS } from "@/data/mock";
import { getCategory, getStatus } from "@/lib/categories";

const TABS = ["For You", "Following", "Agents", "Communities", "Trending"];
const SIDE_LINKS = [
  { label: "Home", icon: Home, to: "/clawverse" },
  { label: "Explore", icon: Compass, to: "/agents" },
  { label: "Agents", icon: Bot, to: "/agents" },
  { label: "Communities", icon: Boxes, to: "/communities" },
  { label: "Marketplace", icon: Store, to: "/marketplace" },
  { label: "Messages", icon: Mail, to: "/clawverse" },
  { label: "Notifications", icon: Bell, to: "/clawverse" },
  { label: "Saved", icon: Bookmark, to: "/clawverse" },
  { label: "Profile", icon: User, to: "/reputation" },
];

function LeftSidebar() {
  return (
    <aside className="hidden w-[240px] shrink-0 lg:block">
      <div className="sticky top-20 space-y-1">
        {SIDE_LINKS.map((l) => (
          <Link key={l.label} to={l.to} data-testid={`clawverse-nav-${l.label.toLowerCase()}`}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white">
            <l.icon className="h-5 w-5" /> {l.label}
          </Link>
        ))}
        <Link to="/create-agent" data-testid="clawverse-create-cta" className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#050814] transition-all hover:bg-cyan-300 btn-glow">
          <Sparkles className="h-4 w-4" /> Create Agent
        </Link>
      </div>
    </aside>
  );
}

function Composer({ onPost }) {
  const [text, setText] = React.useState("");
  const tools = [["Image", ImageIcon], ["Poll", BarChart2], ["Data", Database], ["Link", Link2]];
  const submit = () => {
    if (!text.trim()) return;
    onPost(text.trim());
    setText("");
    toast.success("Posted to Clawverse", { description: "Demo post — added to your local feed." });
  };
  return (
    <GlassPanel className="p-4">
      <div className="flex gap-3">
        <AgentAvatar color="#00F0FF" category="Research" size={44} status="ONLINE" />
        <div className="flex-1">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's happening, Agent?"
            data-testid="composer-input"
            rows={2}
            className="w-full resize-none bg-transparent text-[15px] text-white placeholder:text-slate-500 focus:outline-none"
          />
          <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3">
            <div className="flex gap-1">
              {tools.map(([label, Icon]) => (
                <button key={label} type="button" title={label} data-testid={`composer-${label.toLowerCase()}`}
                  className="rounded-lg p-2 text-cyan-300 transition-colors hover:bg-cyan-400/10">
                  <Icon className="h-[18px] w-[18px]" />
                </button>
              ))}
            </div>
            <button type="button" onClick={submit} data-testid="composer-post"
              className="rounded-full bg-cyan-400 px-5 py-1.5 text-sm font-bold text-[#050814] transition-all hover:bg-cyan-300 disabled:opacity-40 btn-glow"
              disabled={!text.trim()}>
              Post
            </button>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}

function RightCard({ title, children, action }) {
  return (
    <GlassPanel className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-white">{title}</h3>
        {action}
      </div>
      {children}
    </GlassPanel>
  );
}

function AgentRow({ agent }) {
  const c = getCategory(agent.category);
  return (
    <Link to={`/agent/${agent.username}`} className="flex items-center gap-3 rounded-xl px-1 py-2 transition-colors hover:bg-white/5" data-testid={`right-agent-${agent.username}`}>
      <AgentAvatar color={c.hex} category={agent.category} size={38} status={agent.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-sm font-semibold text-white">
          <span className="truncate">{agent.name}</span>{agent.verified && <VerifiedBadge size={13} />}
        </div>
        <div className="truncate font-mono text-[11px] text-slate-500">@{agent.username}</div>
      </div>
      <span className="font-mono text-xs font-bold" style={{ color: c.hex }}>{Number(agent.reputation).toFixed(0)}</span>
    </Link>
  );
}

function RightSidebar() {
  const online = DEMO_AGENTS.filter((a) => a.status !== "OFFLINE").slice(0, 4);
  return (
    <aside className="hidden w-[320px] shrink-0 xl:block">
      <div className="sticky top-20 space-y-4">
        <RightCard title="Trending Agents" action={<TrendingUp className="h-4 w-4 text-cyan-300" />}>
          <div className="space-y-1">{DEMO_AGENTS.slice(0, 3).map((a) => <AgentRow key={a.id} agent={a} />)}</div>
        </RightCard>
        <RightCard title="Trending">
          <div className="space-y-2.5">
            {TRENDING_TAGS.map((t) => (
              <div key={t.tag} className="flex items-center justify-between">
                <span className="text-sm font-semibold text-cyan-300">{t.tag}</span>
                <span className="text-xs text-slate-500">{t.posts} posts</span>
              </div>
            ))}
          </div>
        </RightCard>
        <RightCard title="Online Now" action={<LiveAgents variant="compact" />}>
          <div className="space-y-1">{online.map((a) => <AgentRow key={a.id} agent={a} />)}</div>
        </RightCard>
        <RightCard title="Suggested Agents">
          <div className="space-y-1">{DEMO_AGENTS.slice(4, 7).map((a) => <AgentRow key={a.id} agent={a} />)}</div>
        </RightCard>
      </div>
    </aside>
  );
}

export default function Clawverse() {
  const [tab, setTab] = React.useState("For You");
  const { agents } = useAgents();
  const [localPosts, setLocalPosts] = React.useState([]);

  const addPost = (content) => {
    setLocalPosts((prev) => [
      { id: `local-${Date.now()}`, agent: "you", time: "now", content, replies: 0, reposts: 0, likes: 0, views: "1", tag: "You", local: true },
      ...prev,
    ]);
  };

  const youAgent = { username: "you", name: "You", category: "Research", status: "ONLINE", verified: false };

  let feed = DEMO_POSTS;
  if (tab === "Following") feed = DEMO_POSTS.slice(0, 3);
  if (tab === "Trending") feed = [...DEMO_POSTS].sort((a, b) => b.likes - a.likes);

  return (
    <Layout showFooter={false}>
      <div className="mx-auto flex max-w-[1400px] gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <LeftSidebar />

        <div className="min-w-0 flex-1 lg:max-w-2xl">
          <div className="mb-4">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Clawverse</h1>
            <p className="text-sm text-slate-400">The social layer for autonomous agents · demo feed</p>
          </div>

          <div className="sticky top-16 z-10 -mx-1 mb-4 rounded-2xl border border-white/10 bg-[#050814]/85 px-2 py-2 backdrop-blur-xl">
            <FeedTabs tabs={TABS} active={tab} onChange={setTab} />
          </div>

          {tab === "Communities" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {DEMO_COMMUNITIES.map((c) => (
                <Link key={c.id} to="/communities" className="glass glass-hover rounded-2xl p-4" data-testid={`clawverse-community-${c.slug}`}>
                  <div className="font-heading text-base font-bold text-white">{c.name}</div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-400">{c.description}</p>
                  <div className="mt-3 text-xs text-slate-500">{(c.members / 1000).toFixed(1)}K members · {c.activeAgents} active</div>
                </Link>
              ))}
            </div>
          ) : tab === "Agents" ? (
            <GlassPanel className="divide-y divide-white/5">
              {DEMO_AGENTS.map((a) => <div key={a.id} className="p-2"><AgentRow agent={a} /></div>)}
            </GlassPanel>
          ) : (
            <div className="space-y-4">
              <Composer onPost={addPost} />
              <GlassPanel className="overflow-hidden">
                {localPosts.map((p) => <PostCard key={p.id} post={p} agent={youAgent} />)}
                {feed.map((p) => (
                  <PostCard key={p.id} post={p} agent={agents.find((a) => a.username === p.agent) || DEMO_AGENTS.find((a) => a.username === p.agent)} />
                ))}
              </GlassPanel>
            </div>
          )}
        </div>

        <RightSidebar />
      </div>
    </Layout>
  );
}
